<template>
  <div class="multi-monitor">
    <v-card class="white mb-4" elevation="2">
      <v-card-text class="pa-6">
        <div class="d-flex flex-wrap align-center">
          <div>
            <div class="text-h5 font-weight-bold">Monitoreo de Todas las Maquinas</div>
            <div class="text-body-2 text--secondary">
              Si en 5 minutos no llega dato, la maquina se marca en paro. Si no hay datos del sensor, se marca inactiva.
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-chip color="primary" class="mr-2 mt-2 mt-sm-0">
            <v-icon left small>mdi-clock-outline</v-icon>
            Ultima revision: {{ ultimaRevisionTexto }}
          </v-chip>
          <v-chip :color="requestInFlight ? 'warning' : 'success'" class="mt-2 mt-sm-0">
            <v-icon left small>{{ requestInFlight ? 'mdi-sync' : 'mdi-check-circle' }}</v-icon>
            {{ requestInFlight ? 'Actualizando' : 'Listo' }}
          </v-chip>
        </div>

        <v-row class="mt-4">
          <v-col cols="12" md="4">
            <v-select
              v-model="filtros.area"
              :items="areasConTodas"
              item-text="nombre"
              item-value="idArea"
              label="Area"
              dense
              outlined
              hide-details
              @change="refrescarMonitoreo"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="pollMs"
              :items="intervalos"
              item-text="text"
              item-value="value"
              label="Autoactualizacion"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filtros.estado"
              :items="filtrosEstado"
              item-text="text"
              item-value="value"
              label="Mostrar"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn color="primary" block :loading="requestInFlight" @click="refrescarMonitoreo">
              <v-icon left>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row class="mb-2">
      <v-col cols="6" sm="3" lg="2">
        <v-card class="summary-card summary-activa" outlined>
          <v-card-text class="pa-4">
            <div class="text-caption text-uppercase">Activas</div>
            <div class="text-h4 font-weight-bold">{{ resumen.activa }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3" lg="2">
        <v-card class="summary-card summary-paro" outlined>
          <v-card-text class="pa-4">
            <div class="text-caption text-uppercase">En Paro</div>
            <div class="text-h4 font-weight-bold">{{ resumen.paro }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3" lg="2">
        <v-card class="summary-card summary-inactiva" outlined>
          <v-card-text class="pa-4">
            <div class="text-caption text-uppercase">Inactivas</div>
            <div class="text-h4 font-weight-bold">{{ resumen.inactiva }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3" lg="2">
        <v-card class="summary-card summary-error" outlined>
          <v-card-text class="pa-4">
            <div class="text-caption text-uppercase">Con Error</div>
            <div class="text-h4 font-weight-bold">{{ resumen.error }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3" lg="2">
        <v-card class="summary-card summary-desfase" outlined>
          <v-card-text class="pa-4">
            <div class="text-caption text-uppercase">Desfasadas</div>
            <div class="text-h4 font-weight-bold">{{ resumen.desfase }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="4">
        <v-card class="summary-card" outlined>
          <v-card-text class="pa-4">
            <div class="text-caption text-uppercase">Maquinas evaluadas</div>
            <div class="d-flex align-center justify-space-between">
              <div class="text-h4 font-weight-bold">{{ maquinasFiltradas.length }}</div>
              <div class="text-body-2 text--secondary text-right">
                {{ resumen.activa + resumen.paro + resumen.inactiva + resumen.error }} clasificadas
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="loadingInicial && !maquinasMonitor.length">
      <v-col cols="12">
        <v-card class="white" elevation="1">
          <v-card-text class="pa-8 text-center">
            <v-progress-circular indeterminate color="primary" size="52"></v-progress-circular>
            <div class="text-subtitle-1 mt-4">Consultando sensores en tiempo real...</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else-if="!maquinasFiltradas.length">
      <v-col cols="12">
        <v-alert border="left" colored-border color="info" elevation="1">
          No hay maquinas para mostrar con los filtros actuales.
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col
        v-for="maquina in maquinasFiltradas"
        :key="maquina.idMaquinaIns"
        cols="12"
        sm="6"
        md="4"
        xl="3"
      >
        <v-card class="machine-card fill-height" :class="`machine-card--${maquina.status}`" outlined>
          <v-card-text class="pa-5">
            <div class="d-flex align-start">
              <div class="flex-grow-1">
                <div class="text-overline machine-area">{{ maquina.areaNombre }}</div>
                <div class="text-h6 font-weight-bold machine-name">{{ maquina.nombreCorto }}</div>
                <div class="text-body-2 text--secondary">{{ maquina.nombreInstancia }}</div>
              </div>
              <v-chip small dark :color="maquina.color">
                <v-icon left x-small>{{ maquina.icono }}</v-icon>
                {{ maquina.statusLabel }}
              </v-chip>
            </div>

            <v-divider class="my-4"></v-divider>

            <div class="metric-grid">
              <div class="metric-box">
                <div class="metric-label">Ultimo dato</div>
                <div class="metric-value">{{ maquina.ultimoValorTexto }}</div>
              </div>
              <div class="metric-box">
                <div class="metric-label">Sin datos</div>
                <div class="metric-value">{{ maquina.tiempoSinDatosTexto }}</div>
              </div>
            </div>

            <div class="mt-4">
              <div class="text-caption text-uppercase text--secondary mb-1">Ultima lectura</div>
              <div class="text-body-1 font-weight-medium">{{ maquina.ultimaLecturaTexto }}</div>
            </div>

            <div class="mt-3">
              <div class="text-caption text-uppercase text--secondary mb-1">Token</div>
              <div class="text-body-2 font-weight-medium">{{ maquina.token || 'Sin token configurado' }}</div>
            </div>

            <div class="mt-3" v-if="maquina.codigoMolde">
              <div class="text-caption text-uppercase text--secondary mb-1">Molde</div>
              <div class="text-body-2 font-weight-medium">{{ maquina.codigoMolde }}</div>
            </div>

            <div class="mt-3" v-if="maquina.articulo">
              <div class="text-caption text-uppercase text--secondary mb-1">Articulo</div>
              <div class="text-body-2 font-weight-medium">{{ maquina.articulo }}</div>
            </div>

            <div class="mt-4 compare-grid">
              <div class="compare-box">
                <div class="compare-label">Sensor realtime</div>
                <v-chip x-small dark :color="maquina.color">
                  {{ maquina.statusLabel }}
                </v-chip>
              </div>
              <div class="compare-box">
                <div class="compare-label">Sistema EP</div>
                <v-chip x-small dark :color="maquina.sistemaColor">
                  {{ maquina.sistemaStatusLabel }}
                </v-chip>
              </div>
            </div>

            <v-alert
              dense
              text
              class="mt-4 mb-0"
              :color="maquina.color"
              :icon="maquina.icono"
            >
              {{ maquina.detalle }}
            </v-alert>

            <v-alert
              v-if="maquina.desfase"
              dense
              outlined
              color="deep-orange"
              class="mt-3 mb-0"
              icon="mdi-compare"
            >
              {{ maquina.mensajeDesfase }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

const STATUS_META = {
  activa: { label: "Activa", color: "success", icono: "mdi-play-circle" },
  paro: { label: "En paro", color: "error", icono: "mdi-alert-circle" },
  inactiva: { label: "Inactiva", color: "grey", icono: "mdi-pause-circle" },
  error: { label: "Error", color: "warning", icono: "mdi-cloud-alert" },
};

export default {
  name: "GraficaTiempoRealMulti",
  props: {
    active: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      areas: [],
      turnos: [],
      instancias: [],
      maquinasMonitor: [],
      loadingInicial: false,
      requestInFlight: false,
      ultimaRevisionTexto: "---",
      interval: null,
      tokenCache: {},
      pollMs: 60000,
      staleMinutes: 5,
      filtros: {
        area: null,
        estado: "todos",
      },
      intervalos: [
        { text: "Cada 30 segundos", value: 30000 },
        { text: "Cada 1 minuto", value: 60000 },
        { text: "Cada 2 minutos", value: 120000 },
      ],
      filtrosEstado: [
        { text: "Todas", value: "todos" },
        { text: "Solo en paro", value: "paro" },
        { text: "Solo activas", value: "activa" },
        { text: "Solo inactivas", value: "inactiva" },
        { text: "Solo con error", value: "error" },
      ],
    };
  },
  computed: {
    areasConTodas() {
      return [{ idArea: null, nombre: "Todas las areas" }, ...this.areas];
    },
    maquinasBase() {
      if (!this.filtros.area) {
        return this.instancias;
      }
      return this.instancias.filter((item) => item.idArea === this.filtros.area);
    },
    maquinasFiltradas() {
      const prioridad = { paro: 0, error: 1, activa: 2, inactiva: 3 };
      return this.maquinasMonitor
        .filter((item) => this.filtros.estado === "todos" || item.status === this.filtros.estado)
        .sort((a, b) => {
          const diffStatus = (prioridad[a.status] ?? 99) - (prioridad[b.status] ?? 99);
          if (diffStatus !== 0) return diffStatus;
          return (b.minutosSinDatos || 0) - (a.minutosSinDatos || 0);
        });
    },
    resumen() {
      return this.maquinasMonitor.reduce((acc, item) => {
        acc[item.status] = (acc[item.status] || 0) + 1;
        if (item.desfase) {
          acc.desfase += 1;
        }
        return acc;
      }, { activa: 0, paro: 0, inactiva: 0, error: 0, desfase: 0 });
    },
  },
  watch: {
    active(valor) {
      if (valor) {
        this.iniciarPolling();
        this.refrescarMonitoreo();
      } else {
        this.detenerPolling();
      }
    },
    pollMs() {
      this.iniciarPolling();
    },
  },
  mounted() {
    this.cargarCatalogos();
  },
  beforeDestroy() {
    this.detenerPolling();
  },
  methods: {
    async cargarCatalogos() {
      this.loadingInicial = true;
      try {
        const [areasResp, instanciasResp, turnosResp] = await Promise.all([
          axios.get("/Areas"),
          axios.get("/MaquinaInstancias"),
          axios.get("/Turnos"),
        ]);
        this.areas = Array.isArray(areasResp.data) ? areasResp.data.filter((a) => a.idArea > 1) : [];
        this.instancias = Array.isArray(instanciasResp.data) ? instanciasResp.data : [];
        this.turnos = Array.isArray(turnosResp.data) ? turnosResp.data : [];
        await this.refrescarMonitoreo();
        this.iniciarPolling();
      } catch (error) {
        console.error("Error cargando catalogos de monitoreo:", error);
      } finally {
        this.loadingInicial = false;
      }
    },
    iniciarPolling() {
      this.detenerPolling();
      if (!this.active) return;
      this.interval = setInterval(() => {
        this.refrescarMonitoreo();
      }, this.pollMs);
    },
    detenerPolling() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    async refrescarMonitoreo() {
      if (this.requestInFlight || !this.maquinasBase.length) {
        this.maquinasMonitor = this.maquinasBase.length ? this.maquinasMonitor : [];
        return;
      }

      this.requestInFlight = true;
      try {
        const resultados = await Promise.allSettled(
          this.maquinasBase.map((maquina) => this.obtenerSnapshotMaquina(maquina))
        );

        this.maquinasMonitor = resultados.map((resultado, index) => {
          if (resultado.status === "fulfilled") {
            return resultado.value;
          }
          return this.crearEstadoError(this.maquinasBase[index], resultado.reason);
        });
        this.ultimaRevisionTexto = moment().format("DD/MM/YYYY HH:mm:ss");
      } finally {
        this.requestInFlight = false;
      }
    },
    async obtenerSnapshotMaquina(maquina) {
      const [token, estadoSistema] = await Promise.all([
        this.obtenerTokenPrimario(maquina.idMaquinaIns),
        this.obtenerEstadoSistema(maquina.idMaquinaIns),
      ]);
      const base = this.crearBaseMaquina(maquina, token);

      if (!token) {
        const desfase = this.hayDesfase("inactiva", estadoSistema.sistemaStatus);
        return {
          ...base,
          status: "inactiva",
          statusLabel: STATUS_META.inactiva.label,
          color: STATUS_META.inactiva.color,
          icono: STATUS_META.inactiva.icono,
          detalle: "No hay token configurado para esta maquina.",
          ...estadoSistema,
          desfase,
          mensajeDesfase: this.construirMensajeDesfase("inactiva", estadoSistema.sistemaStatus),
        };
      }

      const datosRecientes = await this.consultarSerie(maquina.idMaquinaIns, token, this.staleMinutes, "minutes");
      if (datosRecientes.length) {
        const ultimo = datosRecientes[datosRecientes.length - 1];
        const minutosSinDatos = this.calcularMinutosSinDatos(ultimo.x);
        const desfase = this.hayDesfase("activa", estadoSistema.sistemaStatus);
        return {
          ...base,
          status: "activa",
          statusLabel: STATUS_META.activa.label,
          color: STATUS_META.activa.color,
          icono: STATUS_META.activa.icono,
          ultimoValorTexto: `${Number(ultimo.y).toFixed(2)}s`,
          ultimaLecturaTexto: moment(Number(ultimo.x)).format("DD/MM/YYYY HH:mm:ss"),
          tiempoSinDatosTexto: minutosSinDatos < 1 ? "< 1 min" : `${minutosSinDatos} min`,
          minutosSinDatos,
          detalle: `Recibio ${datosRecientes.length} dato(s) dentro de los ultimos ${this.staleMinutes} minutos.`,
          ...estadoSistema,
          desfase,
          mensajeDesfase: this.construirMensajeDesfase("activa", estadoSistema.sistemaStatus),
        };
      }

      const datosHistoricos = await this.consultarSerie(maquina.idMaquinaIns, token, 24, "hours");
      if (datosHistoricos.length) {
        const ultimo = datosHistoricos[datosHistoricos.length - 1];
        const minutosSinDatos = this.calcularMinutosSinDatos(ultimo.x);
        const desfase = this.hayDesfase("paro", estadoSistema.sistemaStatus);
        return {
          ...base,
          status: "paro",
          statusLabel: STATUS_META.paro.label,
          color: STATUS_META.paro.color,
          icono: STATUS_META.paro.icono,
          ultimoValorTexto: `${Number(ultimo.y).toFixed(2)}s`,
          ultimaLecturaTexto: moment(Number(ultimo.x)).format("DD/MM/YYYY HH:mm:ss"),
          tiempoSinDatosTexto: `${minutosSinDatos} min`,
          minutosSinDatos,
          detalle: `No ha recibido datos en los ultimos ${this.staleMinutes} minutos.`,
          ...estadoSistema,
          desfase,
          mensajeDesfase: this.construirMensajeDesfase("paro", estadoSistema.sistemaStatus),
        };
      }

      const desfase = this.hayDesfase("inactiva", estadoSistema.sistemaStatus);
      return {
        ...base,
        status: "inactiva",
        statusLabel: STATUS_META.inactiva.label,
        color: STATUS_META.inactiva.color,
        icono: STATUS_META.inactiva.icono,
        detalle: "El sensor existe pero no reporta datos en el historial reciente.",
        ...estadoSistema,
        desfase,
        mensajeDesfase: this.construirMensajeDesfase("inactiva", estadoSistema.sistemaStatus),
      };
    },
    crearBaseMaquina(maquina, token) {
      const nombreCorto = [maquina.marca, maquina.modelo]
        .filter(Boolean)
        .map((valor) => String(valor).trim())
        .join(" - ");

      return {
        idMaquinaIns: maquina.idMaquinaIns,
        nombreCorto: nombreCorto || maquina.nombre || `Maquina ${maquina.idMaquinaIns}`,
        nombreInstancia: maquina.nombre || "Sin nombre",
        areaNombre: this.obtenerNombreArea(maquina.idArea),
        token,
        ultimoValorTexto: "--",
        ultimaLecturaTexto: "Sin datos",
        tiempoSinDatosTexto: "Sin datos",
        minutosSinDatos: null,
        codigoMolde: null,
        articulo: null,
        sistemaStatus: "error",
        sistemaStatusLabel: "Sin EP",
        sistemaColor: "warning",
        desfase: false,
        mensajeDesfase: "",
      };
    },
    crearEstadoError(maquina, error) {
      const base = this.crearBaseMaquina(maquina, null);
      return {
        ...base,
        status: "error",
        statusLabel: STATUS_META.error.label,
        color: STATUS_META.error.color,
        icono: STATUS_META.error.icono,
        detalle: `No se pudo consultar esta maquina. ${error?.message || ""}`.trim(),
        sistemaStatus: "error",
        sistemaStatusLabel: "Sin EP",
        sistemaColor: "warning",
        desfase: false,
        mensajeDesfase: "",
      };
    },
    async obtenerEstadoSistema(idMaquinaIns) {
      try {
        const { data } = await axios.get("/OEECalculos/HoraPorHora", {
          params: this.buildParams(idMaquinaIns),
        });
        const sistemaStatus = this.determinarEstadoSistema(data);
        return {
          sistemaStatus,
          sistemaStatusLabel: this.obtenerLabelSistema(sistemaStatus),
          sistemaColor: this.obtenerColorSistema(sistemaStatus),
          codigoMolde: this.obtenerCodigoMolde(data),
          articulo: this.obtenerArticulo(data),
        };
      } catch (error) {
        console.error(`Error obteniendo EP de maquina ${idMaquinaIns}:`, error);
        return {
          sistemaStatus: "error",
          sistemaStatusLabel: "Sin EP",
          sistemaColor: "warning",
          codigoMolde: null,
          articulo: null,
        };
      }
    },
    obtenerNombreArea(idArea) {
      return this.areas.find((area) => area.idArea === idArea)?.nombre || "Sin area";
    },
    buildParams(idMaquinaIns) {
      return {
        idMaquinaIns,
        fechaRev: this.hoyMX(),
        idTurno: this.turnoActualId(),
        sinDisponibilidad: false,
        desConParosNP: true,
      };
    },
    hoyMX() {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Mexico_City",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
    },
    minutosHoyMX() {
      const [hh, mm] = new Intl.DateTimeFormat("en-GB", {
        timeZone: "America/Mexico_City",
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date()).split(":").map(Number);
      return hh * 60 + mm;
    },
    rangoMinutos(actual, inicio, fin) {
      return inicio <= fin ? actual >= inicio && actual < fin : actual >= inicio || actual < fin;
    },
    timeToMin(hms = "00:00:00") {
      const [hh = "00", mm = "00"] = String(hms).split(":");
      return Number(hh) * 60 + Number(mm);
    },
    turnoActualId() {
      const minutos = this.minutosHoyMX();
      if (Array.isArray(this.turnos) && this.turnos.length) {
        for (const turno of this.turnos) {
          if (this.rangoMinutos(minutos, this.timeToMin(turno.inicio), this.timeToMin(turno.fin))) {
            return Number(turno.idTurno);
          }
        }
      }
      return 1;
    },
    determinarEstadoSistema(payload) {
      const resumen = payload?.resumen || {};
      const horas = Array.isArray(payload?.horas) ? payload.horas : [];
      const ordenes = Array.isArray(payload?.ordenes) ? payload.ordenes : [];
      const enParo = this.booly(resumen.hayParoEnCurso ?? resumen.HayParoEnCurso);
      if (enParo) return "paro";
      if (ordenes.length > 0) return "activa";

      const slot = horas.find((hora) => hora?.pasadoOEnCurso === true) || this.slotActual(horas);
      if (!slot) return "inactiva";

      const cavidades = Array.isArray(slot?.cavidades) ? slot.cavidades : [];
      const piezasOk = cavidades.reduce((acc, cavidad) => acc + (Number(cavidad?.piezasOk) || 0), 0);
      return piezasOk > 0 || Number(slot?.piezasRateHora || 0) > 0 ? "activa" : "paro";
    },
    obtenerCodigoMolde(payload) {
      const resumen = payload?.resumen || {};
      return payload?.codigoMolde || resumen?.codigoMolde || resumen?.CodigoMolde || null;
    },
    obtenerArticulo(payload) {
      const resumenArticulos = payload?.resumen?.resumenArticulos;
      const articulos = Array.isArray(resumenArticulos?.articulos) ? resumenArticulos.articulos : [];
      if (articulos.length === 1) {
        return articulos[0]?.nombre || articulos[0]?.codigo || null;
      }
      if (articulos.length > 1) {
        return articulos
          .map((item) => item?.codigo || item?.nombre)
          .filter(Boolean)
          .join(", ");
      }

      const horas = Array.isArray(payload?.horas) ? payload.horas : [];
      const codigos = [];
      horas.forEach((hora) => {
        const cavidades = Array.isArray(hora?.cavidades) ? hora.cavidades : [];
        cavidades.forEach((cavidad) => {
          const articulosHora = Array.isArray(cavidad?.articulos) ? cavidad.articulos : [];
          articulosHora.forEach((articulo) => {
            if (articulo?.codigo || articulo?.nombre) {
              codigos.push(articulo.codigo || articulo.nombre);
            }
          });
        });
      });

      const unicos = [...new Set(codigos)];
      return unicos.length ? unicos.join(", ") : null;
    },
    slotActual(horas) {
      const nowMin = this.minutosHoyMX();
      return (horas || []).find((hora) => {
        const [dh, dm] = String(hora?.desdeHora || "00:00").split(":").map(Number);
        const [hh, hm] = String(hora?.hastaHora || "00:00").split(":").map(Number);
        return this.rangoMinutos(nowMin, dh * 60 + (dm || 0), hh * 60 + (hm || 0));
      }) || null;
    },
    booly(valor) {
      return valor === true || valor === 1 || valor === "1";
    },
    obtenerLabelSistema(status) {
      if (status === "activa") return "Activa";
      if (status === "paro") return "En paro";
      if (status === "inactiva") return "Inactiva";
      return "Sin EP";
    },
    obtenerColorSistema(status) {
      if (status === "activa") return "success";
      if (status === "paro") return "error";
      if (status === "inactiva") return "grey";
      return "warning";
    },
    hayDesfase(sensorStatus, sistemaStatus) {
      if (sensorStatus === "error" || sistemaStatus === "error") return false;
      return sensorStatus !== sistemaStatus;
    },
    construirMensajeDesfase(sensorStatus, sistemaStatus) {
      if (!this.hayDesfase(sensorStatus, sistemaStatus)) return "";
      return `Sensor realtime: ${this.obtenerLabelSistema(sensorStatus)} | Sistema EP: ${this.obtenerLabelSistema(sistemaStatus)}`;
    },
    async obtenerTokenPrimario(idMaquinaIns) {
      if (this.tokenCache[idMaquinaIns]) {
        return this.tokenCache[idMaquinaIns];
      }
      try {
        const { data } = await axios.get("/SensorDatos/TokensPorMaquina", {
          params: { idMaquinaIns },
        });
        const token = Array.isArray(data) && data.length ? data[0] : null;
        this.$set(this.tokenCache, idMaquinaIns, token);
        return token;
      } catch (error) {
        console.error(`Error obteniendo token de maquina ${idMaquinaIns}:`, error);
        this.$set(this.tokenCache, idMaquinaIns, null);
        return null;
      }
    },
    async consultarSerie(idMaquinaIns, key, cantidad, unidad) {
      const fin = moment();
      const inicio = moment().subtract(cantidad, unidad);
      const { data } = await axios.get("/SensorDatos/SeriesRealtime", {
        params: {
          inicio: inicio.format("YYYY-MM-DD HH:mm:ss"),
          fin: fin.format("YYYY-MM-DD HH:mm:ss"),
          idMaquinaIns,
          key,
        },
      });

      const serie = Array.isArray(data?.series) && data.series.length ? data.series[0] : null;
      if (!serie || !Array.isArray(serie.data)) {
        return [];
      }

      return serie.data
        .map((punto) => ({
          x: Number(punto.x),
          y: Number(punto.y),
        }))
        .filter((punto) => Number.isFinite(punto.x) && Number.isFinite(punto.y))
        .sort((a, b) => a.x - b.x);
    },
    calcularMinutosSinDatos(timestamp) {
      const diffMs = Math.max(0, moment().valueOf() - Number(timestamp));
      return Math.floor(diffMs / 60000);
    },
  },
};
</script>

<style scoped>
.multi-monitor {
  min-height: 320px;
}

.summary-card {
  border-radius: 16px !important;
}

.summary-activa {
  border-color: rgba(76, 175, 80, 0.35) !important;
}

.summary-paro {
  border-color: rgba(244, 67, 54, 0.35) !important;
}

.summary-inactiva {
  border-color: rgba(96, 125, 139, 0.35) !important;
}

.summary-error {
  border-color: rgba(255, 152, 0, 0.35) !important;
}

.summary-desfase {
  border-color: rgba(255, 111, 0, 0.35) !important;
}

.machine-card {
  border-radius: 18px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.machine-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08) !important;
}

.machine-card--activa {
  border-top: 4px solid #4caf50 !important;
}

.machine-card--paro {
  border-top: 4px solid #f44336 !important;
}

.machine-card--inactiva {
  border-top: 4px solid #78909c !important;
}

.machine-card--error {
  border-top: 4px solid #ff9800 !important;
}

.machine-area {
  letter-spacing: 0.08em;
}

.machine-name {
  line-height: 1.2;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.metric-box {
  padding: 12px;
  border-radius: 12px;
  background: #f8fafc;
}

.compare-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.compare-box {
  padding: 12px;
  border-radius: 12px;
  background: #fff7ed;
}

.compare-label {
  margin-bottom: 8px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9a3412;
}

.metric-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.metric-value {
  margin-top: 6px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
}

@media (max-width: 600px) {
  .metric-grid {
    grid-template-columns: 1fr;
  }

  .compare-grid {
    grid-template-columns: 1fr;
  }
}
</style>
